import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

import useMathJax from 'Util/Hooks/useMathJax';
import Loading from 'Components/Loading';

// By default, if is not Native, onLinkClick will be define to open as a new tab.
//  REF ISSUE: SKOOLDIO-4989
const defaultOnLinkClick = (node) => {
  node.target = '_blank';
};

const addOnClickEventHandler = (contentBoxRef, onLinkClick) => {
  if (contentBoxRef.current.innerHTML && typeof onLinkClick === 'function') {
    const aTagList = contentBoxRef.current.querySelectorAll('a');
    aTagList.forEach((node) => onLinkClick(node));
  }
};

const defaultRenderLoading = () => <Loading width={24} height={24} />;

const youtubeUrlRegex = /^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+/;
const codeSandboxUrlRegex = /^(http(s)?:\/\/)?((w){3}.)?codesandbox\.io\/embed\/.+/;

const isAllowedIframeSource = (srcAttribute) =>
  srcAttribute && (youtubeUrlRegex.test(srcAttribute) || codeSandboxUrlRegex.test(srcAttribute));

// SKOOLDIO-5511: add filter iframe tag function that allow only src is youtube url
DOMPurify.addHook('beforeSanitizeElements', (currentNode) => {
  if (currentNode.tagName === 'IFRAME') {
    const srcAttribute = currentNode.getAttribute('src');
    if (isAllowedIframeSource(srcAttribute)) {
      return currentNode;
    }
    currentNode.remove();
  }
});

const MathJaxPreview = ({
  rawContent,
  script,
  config,
  children,
  className,
  style,
  isShowLoading,
  renderLoading = defaultRenderLoading,
  onLinkClick = defaultOnLinkClick,
}) => {
  const sanitizedMath = DOMPurify.sanitize(rawContent, { ADD_TAGS: ['iframe'] });
  const { ref, isTransforming } = useMathJax(sanitizedMath, { script, config });

  useEffect(() => {
    addOnClickEventHandler(ref, onLinkClick);
  }, [onLinkClick, ref]);

  const renderLoadingView = () => {
    if (!isShowLoading || !isTransforming) return null;
    return renderLoading();
  };

  return (
    <Fragment>
      {renderLoadingView()}
      <div
        ref={ref}
        className={className}
        style={{
          width: '100%',
          visibility: isShowLoading && isTransforming ? 'hidden' : 'visible',
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedMath || children }}
      />
    </Fragment>
  );
};

MathJaxPreview.propTypes = {
  script: PropTypes.string,
  config: PropTypes.object,
  className: PropTypes.string,
  style: PropTypes.object,
  renderLoading: PropTypes.func,
  isShowLoading: PropTypes.bool,
};
MathJaxPreview.defaultProps = {
  script:
    'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.6/MathJax.js?config=TeX-MML-AM_HTMLorMML',
};

export default MathJaxPreview;
